import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { ob, fadeSlideIn } from '../../onboardingV2.tokens';

export const Page = styled.div`
    min-height: 100vh;
    min-height: 100dvh;
    background: ${ob.color.surface};
    color: ${ob.color.text};
    font-family: ${ob.font.body};
    -webkit-font-smoothing: antialiased;

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    ${ob.bp.sm} {
        background: ${ob.color.bgPage};
        padding: 40px 16px;
    }
`;

/** Centers the card; on desktop places the live store preview to its right. */
export const Layout = styled.div`
    width: 100%;

    @media (min-width: 1024px) {
        display: flex;
        gap: 40px;
        justify-content: center;
        align-items: flex-start;
    }
`;

export const Card = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    margin: 0 auto;
    background: ${ob.color.surface};

    ${ob.bp.sm} {
        max-width: 480px;
        min-height: 0;
        border-radius: ${ob.radius.xl};
        box-shadow: ${ob.shadow.card};
        border: 1px solid ${ob.color.borderSubtle};
    }

    @media (min-width: 1024px) {
        margin: 0;
    }
`;

export const Header = styled.header`
    padding: 0 20px;

    ${ob.bp.sm} {
        padding: 8px 32px 0;
    }
`;

export const TopBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 56px;
`;

export const Brand = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${ob.font.heading};
    font-weight: 700;
    font-size: 18px;
    color: ${ob.color.brand};
    letter-spacing: -0.01em;
`;

export const BrandTile = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 9px;
    background: ${ob.color.brand};

    img {
        width: 13px;
        height: auto;
    }
`;

export const HeaderRight = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
`;

export const LoginLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: ${ob.tap};
    padding: 0 4px;
    font-size: 14px;
    line-height: 1.3;
    color: ${ob.color.body};
    text-decoration: none;
    text-align: right;
    white-space: nowrap;
    border-radius: 8px;

    strong {
        color: ${ob.color.brand};
        font-weight: 700;
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    span {
        display: none;
        @media (min-width: 420px) {
            display: inline;
        }
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${ob.focusRing};
    }
`;

export const DemoBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    padding: 0 10px;
    border-radius: ${ob.radius.pill};
    background: ${ob.color.demoBg};
    border: 1px solid ${ob.color.demoBorder};
    color: ${ob.color.demoText};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;

    i {
        font-size: 12px;
    }
`;

export const BackRow = styled.div`
    margin: 8px 0 0 -12px;
`;

export const Body = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export const Content = styled.div`
    flex: 1;
    padding: 24px 20px 24px;
    animation: ${fadeSlideIn} ${ob.motion.base} ${ob.motion.ease};

    ${ob.bp.sm} {
        padding: 24px 32px 8px;
    }

    ${ob.reducedMotion} {
        animation: none;
    }
`;

export const Footer = styled.div`
    position: ${({ sticky }) => (sticky ? 'sticky' : 'static')};
    bottom: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    padding: 12px 20px calc(16px + env(safe-area-inset-bottom, 0px));
    background: ${ob.color.surface};
    border-top: ${({ sticky }) => (sticky ? `1px solid ${ob.color.borderSubtle}` : 'none')};

    /* Tablet / big phones: same single-column layout as phones, so the CTA stays pinned too. */
    ${ob.bp.sm} {
        padding: 16px 32px calc(20px + env(safe-area-inset-bottom, 0px));
        border-radius: 0 0 ${ob.radius.xl} ${ob.radius.xl};
    }

    /* Desktop (card + side preview): the footer sits at the end of the card. */
    @media (min-width: 1024px) {
        position: static;
        border-top: none;
        padding: 24px 32px 32px;
    }
`;

export const VisuallyHidden = styled.span`
    position: absolute !important;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

/** Desktop (>=1024px): the live preview as a sticky right column. */
export const DesktopPreview = styled.div`
    display: none;

    @media (min-width: 1024px) {
        display: block;
        width: 360px;
        position: sticky;
        top: 32px;
        align-self: flex-start;
    }
`;

/**
 * Mobile / tablet (<1024px): the preview peeks from the bottom of the card as a cut-off phone
 * that fades out, with the sticky CTA staying below it. Tapping it opens the full preview.
 */
export const MobilePreviewPeek = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    height: 380px;
    /* The body has no side padding (each section pads itself), so no negative margins here. */
    margin: 8px 0 -4px;
    overflow: hidden;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    -webkit-mask-image: linear-gradient(to bottom, #000 78%, rgba(0, 0, 0, 0) 100%);
    mask-image: linear-gradient(to bottom, #000 78%, rgba(0, 0, 0, 0) 100%);

    .sv2-caption {
        display: none;
    }

    &:focus-visible {
        outline: 2px solid ${ob.color.brand};
        outline-offset: -2px;
    }

    @media (min-width: 1024px) {
        display: none;
    }
`;

/** "Toca para verla completa" chip over the cut-off phone. */
export const PeekHint = styled.span`
    position: absolute;
    top: 72%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border-radius: ${ob.radius.pill};
    background: rgba(16, 35, 26, 0.88);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: ${ob.shadow.raised};

    i {
        font-size: 16px;
    }
`;

/** Floating "Ver mi tienda" button, above the sticky CTA, while the inline preview is off screen. */
export const PreviewFab = styled.button`
    position: fixed;
    right: 16px;
    bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: ${ob.tap};
    padding: 0 18px;
    border: 0;
    border-radius: ${ob.radius.pill};
    background: ${ob.color.brand};
    color: #fff;
    font-family: ${ob.font.body};
    font-size: 15px;
    font-weight: 600;
    box-shadow: ${ob.shadow.raised};
    cursor: pointer;
    animation: ${fadeSlideIn} 200ms ease-out;
    -webkit-tap-highlight-color: transparent;

    i {
        font-size: 18px;
    }

    @media (min-width: 1024px) {
        display: none;
    }
`;

/** Full-screen sheet with the whole store preview, scaled to fit the screen. */
export const SheetBackdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1300;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 16px;
    background: rgba(9, 20, 14, 0.92);
    font-family: ${ob.font.body};
    animation: ${fadeSlideIn} 180ms ease-out;

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }
`;

export const SheetTitle = styled.p`
    margin: 0;
    color: #fff;
    font-family: ${ob.font.heading};
    font-size: 16px;
    font-weight: 700;
`;

export const SheetClose = styled.button`
    position: absolute;
    top: calc(12px + env(safe-area-inset-top, 0px));
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${ob.tap};
    height: ${ob.tap};
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    font-size: 24px;
    cursor: pointer;
`;

/** Takes the scaled size so the sheet centers the phone without empty space. */
export const SheetStage = styled.div`
    position: relative;
    flex-shrink: 0;
`;

export const SheetScaler = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 320px;
    transform-origin: top left;

    .sv2-caption {
        display: none;
    }
`;
